import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../api/index.js';
import { ApiError } from '../api/client.js';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['user', id], queryFn: () => usersApi.get(id!), enabled: !!id });

  const [form, setForm] = useState<{ email?: string; role?: 'admin' | 'user'; isActive?: boolean; password?: string }>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const updateMut = useMutation({
    mutationFn: () => usersApi.update(id!, form),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user', id] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      setSuccess('User updated');
      setForm({});
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Update failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => usersApi.delete(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
  });

  if (isLoading) return <p>Loading…</p>;
  const u = data!.user;

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/users" style={{ color: 'var(--color-text-muted)', fontSize: '.875rem' }}>← Users</Link>
      </div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1.5rem' }}>{u.username}</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <form onSubmit={(e) => { e.preventDefault(); updateMut.mutate(); }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>Email</label>
              <input type="email" defaultValue={u.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select defaultValue={u.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'user' })}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label>New Password (leave blank to keep)</label>
              <input type="password" minLength={8} onChange={(e) => setForm({ ...form, password: e.target.value || undefined })} />
            </div>
            <div className="form-group">
              <label>Active</label>
              <select defaultValue={u.isActive ? '1' : '0'} onChange={(e) => setForm({ ...form, isActive: e.target.value === '1' })}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button className="btn btn-primary" type="submit" disabled={updateMut.isPending}>Save Changes</button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => { if (confirm('Delete this user?')) deleteMut.mutate(); }}
              disabled={deleteMut.isPending}
            >
              Delete User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
